'use client';

import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { SvgMinus, SvgPlus, SvgTrashCan } from '../icons-eataly';

const STEP_BUTTON = 'border border-[var(--sand-90)] cursor-pointer';
const VALUE_CLASS =
    'w-6 shrink-0 text-center text-sm font-bold leading-5 tracking-[0.14px] text-[var(--neutral-10)] tabular-nums';

export interface QuantitySelectorProps {
    /** Number of line items. Use `1` in cart; parent typically hides this control for `0`. */
    value: number;
    /** Fires with the new quantity, or `0` when the shopper removes the line (trash with quantity 1). */
    onChange: (value: number) => void;
    layout?: 'horizontal' | 'vertical';
    /**
     * Maximum quantity (e.g. stock). When set, the increment control is disabled at the max.
     * Minimum is `1` for a visible line; to remove, the shopper uses trash / decrement to `0` via onChange(0).
     */
    max?: number;
    disabled?: boolean;
    className?: string;
}

export default function QuantitySelector({
    value,
    onChange,
    layout = 'horizontal',
    max,
    disabled = false,
    className,
}: QuantitySelectorProps): ReactNode {
    const { t: tSelector } = useTranslation('quantitySelector');
    const valueId = useId();
    if (value < 1) {
        return null;
    }
    const isAtMax = max != null && value >= max;
    const isVertical = layout === 'vertical';
    const showRemoveIcon = value === 1;

    const decLabel = showRemoveIcon ? tSelector('removeItem') : tSelector('decreaseQuantity');
    const incLabel = tSelector('increaseQuantity');
    const labelId = `qty-${valueId}`;

    return (
        <div
            role="group"
            className={cn(
                'inline-flex bg-[var(--sand-95)] text-[var(--neutral-10)] rounded-[40px]',
                isVertical
                    ? 'w-11 min-w-11 flex-col items-center justify-center gap-1'
                    : 'min-h-11 flex-row items-center gap-3',
                className
            )}
            data-testid="quantity-selector"
            data-layout={layout}
            data-qty={value}
            aria-labelledby={labelId}>
            <span id={labelId} className="sr-only">
                {tSelector('quantityValue', { count: value })}
            </span>
            <Button
                hierarchy="tertiary"
                size="icon-small"
                className={STEP_BUTTON}
                onClick={() => (showRemoveIcon ? onChange(0) : onChange(value - 1))}
                disabled={disabled}
                aria-label={decLabel}
                data-testid={showRemoveIcon ? 'quantity-selector-remove' : 'quantity-selector-decrement'}>
                {showRemoveIcon ? (
                    <SvgTrashCan className="size-5" aria-hidden />
                ) : (
                    <SvgMinus className="size-5" aria-hidden />
                )}
            </Button>
            <span className={VALUE_CLASS} aria-hidden data-testid="quantity-selector-value">
                {value}
            </span>
            <Button
                hierarchy="tertiary"
                size="icon-small"
                className={STEP_BUTTON}
                onClick={() => onChange(value + 1)}
                disabled={disabled || isAtMax}
                aria-label={incLabel}
                data-testid="quantity-selector-increment">
                <SvgPlus className="size-5" aria-hidden />
            </Button>
        </div>
    );
}
